/**
 * price-tax-adjustments.js
 *
 * Shows VAT/GST-inclusive prices to visitors from EU countries, the UK and
 * Australia (country read from window.mdApp_currentCountryCode, set by the
 * geolocation/currency app), and simply reveals prices everywhere else.
 *
 * home SEO/perf pass 2026-10-01: moved out of an inline <script> in
 * sections/footer.liquid into this deferred asset. The tax maths, price
 * parsing/formatting and the per-area handlers below are moved unchanged
 * (only re-indented). What changed, and why:
 *   - The hiding/loading CSS this script used to inject at runtime is now
 *     static, render-blocking CSS in <head> (snippets/price-tax-adjustments.liquid).
 *     Prices stay hidden until marked data-tax-processed="true", so deferring
 *     this file cannot flash a pre-tax price.
 *   - The two setInterval polling loops (every 1s in tax countries, every 2s
 *     elsewhere) and the document.body subtree MutationObservers are replaced by
 *     watchLatePrices(): observers scoped to the roots that actually receive
 *     prices after load, one childList-only observer on <body> for overlays
 *     appended there, the theme editor's shopify:section:load event, and a
 *     bounded sweep at 1 s, 3 s and 6 s (+ window load) that then stops.
 *
 * Contract with the rest of the theme: the <head> CSS only hides prices while
 * <html> has the class tax-prices-pending. This script removes it after its
 * last sweep; the inline failsafe in the snippet removes it if this file never
 * ran or its first pass never finished. So no price can stay hidden forever.
 * Code that inserts price markup after load should do it inside one of the
 * LATE_PRICE_ROOTS below (or directly on <body>) so it is priced before paint.
 */
(function () {
  var started = false;

  // Read by the inline failsafe in snippets/price-tax-adjustments.liquid:
  // present = this file executed; ready = the first pass completed.
  var state = window.F4FPriceTax = { ready: false };

  function revealPrices() {
    document.documentElement.classList.remove('tax-prices-pending');
  }

  // `defer` runs this file after parsing but before DOMContentLoaded, so the
  // listener fires at the same moment the old inline listener did (which is
  // when the app-provided country global is read). The load/complete fallbacks
  // only matter if the file is ever loaded late (e.g. made async): without them
  // the prices hidden by the <head> CSS would never be revealed.
  function start() {
    if (started) return;
    started = true;
    try {
      initPriceTaxAdjustments();
      state.ready = true;
    } catch (error) {
      // Never leave prices hidden because of a bug here; still surface it.
      revealPrices();
      throw error;
    }
  }

  if (document.readyState === 'complete') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
    window.addEventListener('load', start);
  }

  function initPriceTaxAdjustments() {
    // Function to mark all prices as processed (for countries without tax)
    function markAllPricesAsProcessed() {
      // Process all standard price elements
      document.querySelectorAll('[data-js-product-price-original]:not([data-tax-processed="true"]), [data-js-product-price-compare]:not([data-tax-processed="true"]), .item-price strong:not([data-tax-processed="true"]), .price.text-size--smaller span[style*="display:flex"]:not([data-tax-processed="true"]), #CartTotal .text-size--large:not([data-tax-processed="true"]), .price.money.product-page-payment-options-price:not([data-tax-processed="true"]), .gm-product-price .money:not([data-tax-processed="true"])').forEach(function(element) {
        element.setAttribute('data-tax-processed', 'true');
        
        // For payment option prices, make sure they're displayed properly
        if (element.classList && element.classList.contains('product-page-payment-options-price')) {
          element.style.display = 'inline-block';
          
          // Find the corresponding currency converter if any and hide it
          const currencyConverterElement = element.nextElementSibling;
          if (currencyConverterElement && currencyConverterElement.classList.contains('currency-converter-wrapper-amount-box')) {
            currencyConverterElement.style.display = 'none';
          }
        }
      });
      
      // Mark game module product containers as processed
      document.querySelectorAll('.gm-product-price:not([data-tax-processed="true"])').forEach(function(container) {
        container.setAttribute('data-tax-processed', 'true');
      });
      
      // Fix any transparent text in currency converter boxes
      document.querySelectorAll('.currency-converter-amount-box').forEach(function(amountBox) {
        if (amountBox.style.color === 'rgba(0, 0, 0, 0)' || amountBox.style.color === 'transparent') {
          amountBox.style.color = 'rgb(102, 102, 102)';
        }
      });

      // Remove the initial hide style to show regular prices
      const initialHideStyle = document.getElementById('tax-price-initial-hide');
      if (initialHideStyle) {
        initialHideStyle.remove();
      }
    }

    // home SEO/perf pass 2026-10-01: event-driven detection of prices inserted
    // after the first pass. Replaces the setInterval polling and the
    // document.body subtree observers (which fired on every DOM change anywhere
    // on the page, e.g. countdown clocks, sliders and app widgets).
    //
    // Every element the <head> CSS hides, i.e. anything that must become
    // data-tax-processed="true" before it is visible.
    const HIDDEN_PRICE_SELECTOR = [
      '[data-js-product-price-original]:not([data-tax-processed="true"])',
      '[data-js-product-price-compare]:not([data-tax-processed="true"])',
      '.item-price strong:not([data-tax-processed="true"])',
      '.price.text-size--smaller span[style*="display:flex"]:not([data-tax-processed="true"])',
      '#CartTotal .text-size--large:not([data-tax-processed="true"])',
      '.price.money.product-page-payment-options-price:not([data-tax-processed="true"])',
      '.gm-product-price .money:not([data-tax-processed="true"])',
      '.gm-product-price:not([data-tax-processed="true"])'
    ].join(', ');

    // The only places theme code (or the Globo mega menu) inserts price markup
    // after load. Each is observed with childList + subtree; the root itself is
    // never replaced, only its contents, so one observe() per root is enough.
    const LATE_PRICE_ROOTS = [
      '.shopify-section-group-header-group',    // Globo mega menu renders .gm-product-price inside the header nav
      '#site-nav--mobile',                      // mobile menu drawer (section-header.js moves it to <body>)
      '[data-js-search-results]',               // predictive search (component-predictive-search.js swaps innerHTML)
      '#AjaxCartForm',                          // cart drawer + cart page line items (component-cart.js, component-product-form.js)
      '#AjaxCartSubtotal',                      // #CartTotal, re-rendered together with the cart
      'cart-recommendations',                   // component-cart-recommendations.js
      'product-recommendations',                // related/complementary products (component-product-form.js)
      '#CollectionProductGrid',                 // collection + search page filtering/pagination (component-facets.js)
      '.product-page-payment-options-wrapper'   // f4f-product-stock.js swaps the payment plan list
    ];

    // Calls onLatePrices() once per batch of mutations, before paint, whenever
    // a node containing a price matching latePriceSelector is added under one
    // of LATE_PRICE_ROOTS or directly to <body>; plus a bounded sweep for
    // anything inserted in a way the observers cannot see.
    function watchLatePrices(onLatePrices, latePriceSelector) {
      let scheduled = false;

      // A microtask, not requestAnimationFrame: rAF is suspended while a tab
      // or window isn't rendering (background tab, automation), which left
      // Globo menu prices rendered after DOMContentLoaded unprocessed in
      // browser verification. A microtask still runs before the next paint.
      // Guard: if passes keep re-triggering (e.g. an app re-inserting markup in
      // reaction to our edits), fall back to a timer so microtasks can never
      // starve rendering.
      let burstStart = 0;
      let burstCount = 0;
      function schedule() {
        if (scheduled) return;
        scheduled = true;
        const now = Date.now();
        if (now - burstStart > 1000) {
          burstStart = now;
          burstCount = 0;
        }
        burstCount++;
        const run = function() {
          scheduled = false;
          onLatePrices();
        };
        if (burstCount > 30) {
          setTimeout(run, 250);
        } else {
          Promise.resolve().then(run);
        }
      }

      // Cheap filter: text-node churn (our own textContent writes, timers,
      // translation apps) is skipped by the nodeType check before any selector
      // matching runs.
      function containsLatePrice(node) {
        return node.nodeType === 1 &&
          (node.matches(latePriceSelector) || node.querySelector(latePriceSelector) !== null);
      }

      const rootObserver = new MutationObserver(function(mutations) {
        if (scheduled) return;
        for (let i = 0; i < mutations.length; i++) {
          const addedNodes = mutations[i].addedNodes;
          for (let j = 0; j < addedNodes.length; j++) {
            if (containsLatePrice(addedNodes[j])) {
              schedule();
              return;
            }
          }
        }
      });

      function observeRoot(root) {
        // Re-observing a node with the same observer just refreshes its options.
        rootObserver.observe(root, { childList: true, subtree: true });
      }

      function observeRootsWithin(scope) {
        LATE_PRICE_ROOTS.forEach(function(selector) {
          if (scope.matches && scope.matches(selector)) observeRoot(scope);
          scope.querySelectorAll(selector).forEach(observeRoot);
        });
      }

      observeRootsWithin(document);

      // The one body-level observer: childList only, no subtree, so it only
      // sees elements appended straight to <body> (quick-view lightbox from
      // component-lightbox.js, modals, app drawers). A new top-level node that
      // carries prices is processed and then observed like any other root.
      const bodyObserver = new MutationObserver(function(mutations) {
        for (let i = 0; i < mutations.length; i++) {
          const addedNodes = mutations[i].addedNodes;
          for (let j = 0; j < addedNodes.length; j++) {
            const node = addedNodes[j];
            if (node.nodeType !== 1) continue;
            observeRootsWithin(node);
            if (containsLatePrice(node)) {
              observeRoot(node);
              schedule();
            }
          }
        }
      });
      bodyObserver.observe(document.body, { childList: true });

      // Theme editor re-renders a whole section: pick up its roots and prices.
      document.addEventListener('shopify:section:load', function(event) {
        observeRootsWithin(event.target);
        schedule();
      });

      // Bounded safety net (2026-10-01 browser verification follow-up): apps
      // such as Globo render after DOMContentLoaded and may build or move
      // markup in ways the observers can't see (detached trees, classes added
      // after insertion). Re-run the same cheap, idempotent pass a few times
      // while apps settle, then stop: no open-ended polling. After the last
      // sweep, lift the CSS hiding so nothing the passes couldn't process
      // stays invisible; later insertions are still caught by the observers.
      const SWEEP_DELAYS_MS = [1000, 3000, 6000];
      SWEEP_DELAYS_MS.forEach(function(delay, i) {
        setTimeout(function() {
          onLatePrices();
          if (i === SWEEP_DELAYS_MS.length - 1) revealPrices();
        }, delay);
      });
      if (document.readyState !== 'complete') {
        window.addEventListener('load', function() { onLatePrices(); }, { once: true });
      }
    }

    // Get the country code from the JavaScript variable
    const countryCode = window.mdApp_currentCountryCode || '';

    // Tax rates for all European countries and Australia
    const taxRates = {
      // Australia
      'AU': 0.10,  // Australia - 10% GST

      // EU Member States
      'AT': 0.20,  // Austria - 20%
      'BE': 0.21,  // Belgium - 21%
      'BG': 0.20,  // Bulgaria - 20%
      'HR': 0.25,  // Croatia - 25%
      'CY': 0.19,  // Cyprus - 19%
      'CZ': 0.21,  // Czech Republic - 21%
      'DK': 0.25,  // Denmark - 25%
      'EE': 0.20,  // Estonia - 20%
      'FI': 0.24,  // Finland - 24%
      'FR': 0.20,  // France - 20%
      'DE': 0.19,  // Germany - 19%
      'GR': 0.24,  // Greece - 24%
      'HU': 0.27,  // Hungary - 27%
      'IE': 0.23,  // Ireland - 23%
      'IT': 0.22,  // Italy - 22%
      'LV': 0.21,  // Latvia - 21%
      'LT': 0.21,  // Lithuania - 21%
      'LU': 0.17,  // Luxembourg - 17%
      'MT': 0.18,  // Malta - 18%
      'NL': 0.21,  // Netherlands - 21%
      'PL': 0.23,  // Poland - 23%
      'PT': 0.23,  // Portugal - 23%
      'RO': 0.19,  // Romania - 19%
      'SK': 0.20,  // Slovakia - 20%
      'SI': 0.22,  // Slovenia - 22%
      'ES': 0.21,  // Spain - 21%
      'SE': 0.25,  // Sweden - 25%

      // Non-EU European countries
      'GB': 0.20,  // United Kingdom - 20%
      // 'CH': 0.077, // Switzerland - 7.7%
      // 'NO': 0.25,  // Norway - 25%
      // 'IS': 0.24,  // Iceland - 24%
      // 'LI': 0.077, // Liechtenstein - 7.7%
      // 'MC': 0.20,  // Monaco - 20%
      // 'SM': 0.17,  // San Marino - 17%
      // 'VA': 0.22,  // Vatican City (follows Italy) - 22%
      // 'AD': 0.045, // Andorra - 4.5%
      // 'ME': 0.21,  // Montenegro - 21%
      // 'MK': 0.18,  // North Macedonia - 18%
      // 'RS': 0.20,  // Serbia - 20%
      // 'AL': 0.20,  // Albania - 20%
      // 'BA': 0.17   // Bosnia and Herzegovina - 17%
    };

    // Only proceed if we have tax rate for the country
    if (taxRates[countryCode]) {
      const taxRate = taxRates[countryCode];
      // Get the tax name (GST for Australia, VAT for others)
      const taxName = countryCode === 'AU' ? 'GST' : 'VAT';

      // Process initial page load elements
      processAllPrices();

      // Setup listeners for search and cart updates
      setupEventListeners();
      
      // Add a targeted listener for various UI interactions that might reveal prices
      document.addEventListener('click', function(event) {
        // Check what type of element was clicked
        const target = event.target;
        
        // Menu toggles and buttons
        if (target.closest('button') || target.closest('.menu-toggle') || 
            target.closest('.nav-toggle') || target.closest('.menu-icon')) {
          // Wait for menu animation to complete
          setTimeout(function() {
            // First check for game module prices
            const unprocessedModules = document.querySelectorAll('.gm-product-price:not([data-tax-processed="true"])');
            if (unprocessedModules.length > 0) {
              handleGameModulePrices();
            }
            
            // Also check for payment options
            const paymentOptions = document.querySelectorAll('.product-page-payment-options-wrapper');
            if (paymentOptions.length > 0) {
              handlePaymentOptions();
            }
            
            // Only fix display issues if we see payment options
            if (document.querySelector('.product-page-payment-options-wrapper')) {
              setTimeout(fixDisplayIssues, 100);
            }
          }, 300);
        }
        
        // Payment method selectors
        else if (target.closest('.payment-method-selector') || 
                 target.closest('.product-page-payment-options') || 
                 target.closest('[data-payment-button]') ||
                 target.closest('[data-shopify-payment-button]')) {
          // Wait for payment options to update
          setTimeout(function() {
            handlePaymentOptions();
          }, 100);
        }
      });
      
      // Track when payment options were last processed to avoid rapid re-processing
      let lastPaymentProcessTime = 0;
      
      // Also listen for change events which might update payment options
      document.addEventListener('change', function(event) {
        const target = event.target;
        const now = Date.now();
        
        // Only process if at least 1000ms has passed since last process
        if (now - lastPaymentProcessTime < 1000) {
          return;
        }
        
        // Variant selectors, quantity inputs, payment radio buttons
        if (target.tagName === 'SELECT' || 
            target.tagName === 'INPUT' || 
            target.closest('[data-variant-selector]') || 
            target.classList.contains('quantity')) {
          
          // Wait a bit longer for payment options to fully update after currency conversion
          setTimeout(function() {
            // Only reset original prices for elements that haven't been tax-processed yet
            // This prevents compounding tax when clicking between payment options
            document.querySelectorAll('[data-tax-original]:not([data-tax-processed="true"])').forEach(function(el) {
              const priceText = el.textContent.trim();
              const newPrice = parseCurrency(priceText);
              if (!isNaN(newPrice) && newPrice > 0) {
                el.setAttribute('data-tax-original', newPrice.toString());
              }
            });

            // Process payment options with fresh data
            handlePaymentOptions();
            lastPaymentProcessTime = Date.now();
          }, 500);
        }
      });

      // Helper function to fix visibility issues - optimized for performance
      let lastFixTime = 0;
      function fixDisplayIssues() {
        // Throttle execution to at most once every 2 seconds
        const now = Date.now();
        if (now - lastFixTime < 2000) return;
        lastFixTime = now;
        
        // Use a direct CSS solution instead of JavaScript where possible
        const fixStyle = document.getElementById('tax-display-fix-style');
        if (!fixStyle) {
          // Create a style tag to fix display issues using CSS (more efficient)
          const style = document.createElement('style');
          style.id = 'tax-display-fix-style';
          style.textContent = `
            /* Only show prices that don't have currency converters */
            .product-price[data-tax-processed="true"]:not([data-cbb-price-processed="true"]),
            [data-js-product-price-original][data-tax-processed="true"]:not([data-cbb-price-processed="true"]) {
              display: inline-block !important;
            }
            
            /* Keep original price elements hidden if they have currency converter */
            [data-tax-processed="true"][data-cbb-price-processed="true"] {
              display: none !important;
            }
            
            /* Always show currency converters and fix transparent colors */
            .currency-converter-wrapper-amount-box {
              display: inline-block !important;
            }
            
            /* Fix transparent text in currency converters */
            .currency-converter-amount-box[style*="color: rgba(0, 0, 0, 0)"],
            .currency-converter-amount-box[style*="color:rgba(0,0,0,0)"] {
              color: rgb(102, 102, 102) !important;
            }
            
            /* Also fix the children that inherit the transparent color */
            .currency-converter-amount-box[style*="color: rgba(0, 0, 0, 0)"] *,
            .currency-converter-amount-box[style*="color:rgba(0,0,0,0)"] * {
              color: inherit !important;
            }
          `;
          document.head.appendChild(style);
        }
        
        // Make sure original price elements with currency converter processing remain hidden
        document.querySelectorAll('[data-cbb-price-processed="true"][style*="display: inline-block"]').forEach(function(el) {
          el.style.display = 'none';
        });
        
        // Make sure currency converters are always visible and fix transparent colors
        document.querySelectorAll('.currency-converter-wrapper-amount-box').forEach(function(el) {
          // Fix display
          if (el.style.display === 'none') {
            el.style.display = 'inline-block';
          }
          
          // Fix transparent colors
          const amountBox = el.querySelector('.currency-converter-amount-box');
          if (amountBox && (amountBox.style.color === 'rgba(0, 0, 0, 0)' || amountBox.style.color === 'transparent')) {
            amountBox.style.color = 'rgb(102, 102, 102)';
            
            // Also fix child elements
            const digits = amountBox.querySelectorAll('.currency-converter-amount, .cbb-price-digits, .cbb-price-symbol');
            digits.forEach(function(digit) {
              if (digit.style.color === 'inherit' || digit.style.color === 'rgba(0, 0, 0, 0)') {
                digit.style.color = 'rgb(102, 102, 102)';
              }
            });
          }
        });
        
        // Only make non-currency converter prices visible
        const paymentArea = document.querySelector('.product-page-payment-options-wrapper');
        if (paymentArea) {
          const hiddenPrices = paymentArea.querySelectorAll('.product-page-payment-options-price[data-tax-processed="true"]:not([data-cbb-price-processed="true"])[style*="display: none"]');
          for (let i = 0; i < Math.min(hiddenPrices.length, 3); i++) {
            hiddenPrices[i].style.display = 'inline-block';
          }
        }
      }
      
      // Function to handle payment options
      function handlePaymentOptions() {
        // Track whether we processed any elements
        let processedAny = false;
        
        // 1. Handle all payment option prices including converter-processed ones
        // Use a more inclusive selector to catch all payment option prices
        const paymentOptionPrices = document.querySelectorAll('.price.money.product-page-payment-options-price');
        
        paymentOptionPrices.forEach(function(priceEl) {
          // Skip if already fully processed
          if (priceEl.getAttribute('data-tax-processed') === 'true') {
            processedAny = true;
            return;
          }
          
          // Get and process the price (even if it's been processed by the currency converter)
          let priceText = priceEl.textContent.trim();
          let numericValue = parseCurrency(priceText);
          
          // If this element has been processed by currency converter but not tax
          if (priceEl.getAttribute('data-cbb-price-processed') === 'true' && priceEl.style.display === 'none') {
            // Try to get the price from the next element (currency converter)
            const currencyConverterElement = priceEl.nextElementSibling;
            if (currencyConverterElement && currencyConverterElement.classList.contains('currency-converter-wrapper-amount-box')) {
              // Skip if the converter element has already been tax-processed
              if (currencyConverterElement.getAttribute('data-tax-processed') === 'true') {
                processedAny = true;
                return;
              }

              const digitElements = currencyConverterElement.querySelectorAll('.cbb-price-digits');
              if (digitElements.length > 0) {
                // Get price from converter
                priceText = '';
                digitElements.forEach(function(el) {
                  priceText += el.textContent.trim();
                });
                numericValue = parseFloat(priceText);

                // Get original price from the HIDDEN original element, not the converter display
                // The hidden element (priceEl) contains the untaxed price from Shopify
                const originalPriceText = priceEl.textContent.trim();
                const originalFromHidden = parseCurrency(originalPriceText);

                // Use the hidden element's price as the source of truth (it's the pre-tax price)
                // Only use converter element's data-tax-original if hidden element price is invalid
                let originalPrice;
                if (!isNaN(originalFromHidden) && originalFromHidden > 0) {
                  originalPrice = originalFromHidden;
                  // Store this as the original
                  currencyConverterElement.setAttribute('data-tax-original', originalFromHidden.toString());
                } else if (currencyConverterElement.hasAttribute('data-tax-original')) {
                  originalPrice = parseFloat(currencyConverterElement.getAttribute('data-tax-original'));
                } else {
                  originalPrice = numericValue;
                  currencyConverterElement.setAttribute('data-tax-original', numericValue.toString());
                }

                // Calculate price with tax based on ORIGINAL price
                if (!isNaN(originalPrice) && originalPrice > 0) {
                  const priceWithTax = originalPrice * (1 + taxRate);

                  // Update the currency converter display
                  // Only update the FIRST digit element to avoid duplication issues
                  if (digitElements.length > 0) {
                    // Check if the original format used comma as decimal (European format)
                    const originalDigitText = digitElements[0].textContent.trim();
                    const useCommaDecimal = originalDigitText.indexOf(',') > originalDigitText.indexOf('.');

                    // Format the price appropriately
                    let formattedPrice = priceWithTax.toFixed(2);
                    if (useCommaDecimal) {
                      formattedPrice = formattedPrice.replace('.', ',');
                    }

                    digitElements[0].textContent = formattedPrice;

                    // Hide any additional digit elements to prevent duplication
                    for (let i = 1; i < digitElements.length; i++) {
                      digitElements[i].style.display = 'none';
                    }
                  }

                  // Make sure color is visible
                  const amountBox = currencyConverterElement.querySelector('.currency-converter-amount-box');
                  if (amountBox) {
                    amountBox.style.color = 'rgb(102, 102, 102)';
                  }

                  currencyConverterElement.setAttribute('data-tax-processed', 'true');
                  currencyConverterElement.setAttribute('data-color-fixed', 'true');
                  processedAny = true;
                }
              }
            }
          } 
          // Standard price element without currency converter processing
          else if (!isNaN(numericValue)) {
            // Store original price if not already stored
            if (!priceEl.hasAttribute('data-tax-original')) {
              priceEl.setAttribute('data-tax-original', numericValue.toString());
            }
            
            // Get original price - always use this as the source of truth
            const originalPrice = parseFloat(priceEl.getAttribute('data-tax-original')) || numericValue;
            
            // Check if price has already been taxed
            const estimatedTaxedPrice = originalPrice * (1 + taxRate);
            const currentDisplayedPrice = parseCurrency(priceEl.textContent.trim());
            
            // If current price is already within 5% of expected taxed price, it's likely already taxed
            if (!isNaN(currentDisplayedPrice) && 
                currentDisplayedPrice > 0 && 
                Math.abs(currentDisplayedPrice - estimatedTaxedPrice) < estimatedTaxedPrice * 0.05) {
              // Already taxed, just mark as processed if not marked already
              if (!priceEl.hasAttribute('data-tax-processed')) {
                priceEl.setAttribute('data-tax-processed', 'true');
                processedAny = true;
              }
            } else {
              // Make the element visible
              priceEl.style.display = 'inline-block';
              
              // Calculate price with tax based on ORIGINAL price
              const priceWithTax = originalPrice * (1 + taxRate);
              
              // Format money in the same format as original price
              const formattedPrice = formatMoney(priceWithTax, priceText);
              
              // Only update if different (prevent loops with currency converter)
              if (priceEl.textContent.trim() !== formattedPrice) {
                // Set a timestamp to track when this processing happened
                const processingTime = Date.now();
                priceEl.setAttribute('data-tax-processed-time', processingTime.toString());
                
                // Update price display
                priceEl.textContent = formattedPrice;
                
                // Mark as processed
                priceEl.setAttribute('data-tax-processed', 'true');
                
                // Find the corresponding currency converter if any and hide it
                const currencyConverterElement = priceEl.nextElementSibling;
                if (currencyConverterElement && currencyConverterElement.classList.contains('currency-converter-wrapper-amount-box')) {
                  currencyConverterElement.style.display = 'none';
                }
                
                processedAny = true;
              }
            }
          }
        });
        
        // 2. Handle any remaining independent currency converter elements
        const converterElements = document.querySelectorAll('.product-page-payment-options-wrapper .currency-converter-wrapper-amount-box:not([data-color-fixed="true"])');
        
        converterElements.forEach(function(converterEl) {
          // Skip if this converter already has tax applied
          if (converterEl.getAttribute('data-tax-processed') === 'true') {
            return;
          }
          
          // Fix color if transparent
          const amountBox = converterEl.querySelector('.currency-converter-amount-box');
          if (amountBox) {
            // Make color visible
            if (amountBox.style.color === 'rgba(0, 0, 0, 0)' || amountBox.style.color === 'transparent') {
              amountBox.style.color = 'rgb(102, 102, 102)';
            }
            
            // Check if this element is associated with a money element that's already processed
            const prevSibling = converterEl.previousElementSibling;
            if (prevSibling && prevSibling.classList.contains('money') && 
                prevSibling.getAttribute('data-tax-processed') === 'true') {
              // The associated money element is already processed, so mark this one
              converterEl.setAttribute('data-tax-processed', 'true');
            }
            
            // Mark converter as having color fixed
            converterEl.setAttribute('data-color-fixed', 'true');
          }
        });
        
        // 3. Check if we need to add tax info to payment option sections
        // More aggressive selection to ensure we don't miss any
        let wrappers = document.querySelectorAll('.product-page-payment-options-wrapper:not([data-tax-info-added="true"])');
        
        // If none found, check for ones that might need re-processing
        if (wrappers.length === 0 && processedAny) {
          wrappers = document.querySelectorAll('.product-page-payment-options-wrapper');
        }
        
        wrappers.forEach(function(wrapper) {
          // Find all payment option details
          const detailsDivs = wrapper.querySelectorAll('.payment-option-details');
          
          // If no details divs found, look for the main wrapper to add info
          if (detailsDivs.length === 0) {
            if (!wrapper.querySelector('.payment-option-tax-info')) {
              // Create tax info element
              const taxInfoEl = document.createElement('div');
              taxInfoEl.className = 'payment-option-tax-info';
              taxInfoEl.style.fontSize = '0.7rem';
              taxInfoEl.style.color = '#666';
              taxInfoEl.style.marginTop = '4px';
              taxInfoEl.innerHTML = `All prices include ${(taxRate * 100).toFixed(0)}% ${taxName}`;
              
              // Add to the wrapper itself
              wrapper.appendChild(taxInfoEl);
            }
          } else {
            // Add tax info to each details div
            detailsDivs.forEach(function(detailsDiv) {
              // Add tax info if not already present
              if (!detailsDiv.querySelector('.payment-option-tax-info')) {
                // Create tax info element
                const taxInfoEl = document.createElement('div');
                taxInfoEl.className = 'payment-option-tax-info';
                taxInfoEl.style.fontSize = '0.7rem';
                taxInfoEl.style.color = '#666';
                taxInfoEl.style.marginTop = '4px';
                taxInfoEl.innerHTML = `All prices include ${(taxRate * 100).toFixed(0)}% ${taxName}`;
                
                // Add to the details div
                detailsDiv.appendChild(taxInfoEl);
              }
            });
          }
          
          // Mark wrapper as having tax info added
          wrapper.setAttribute('data-tax-info-added', 'true');
        });
      }

      // Function to handle game module prices - optimized for performance
      function handleGameModulePrices() {
        // Use a single DOM query to get all needed elements
        const containers = document.querySelectorAll('.gm-product-price:not([data-tax-processed="true"])');
        if (containers.length === 0) return; // Early exit if nothing to process
        
        containers.forEach(function(container) {
          // Skip if already processed (double-check)
          if (container.getAttribute('data-tax-processed') === 'true') return;
          
          // Try to find either standard money elements or currency converter elements
          const moneyEl = container.querySelector('.money:not([data-tax-processed="true"]):not([data-cbb-price-processed="true"])');
          const hiddenMoneyEl = container.querySelector('.money[data-cbb-price-processed="true"][style*="display: none"]');
          const converterEl = container.querySelector('.currency-converter-wrapper-amount-box[style*="display: inline-block"]');
          
          let processed = false;
          
          // Case 1: Regular money element
          if (moneyEl) {
            // Make the element visible
            moneyEl.style.display = 'inline-block';
            
            // Get and process the price
            const priceText = moneyEl.textContent.trim();
            const numericValue = parseCurrency(priceText);
            
            // Store original price if not already stored
            if (!moneyEl.hasAttribute('data-tax-original')) {
              moneyEl.setAttribute('data-tax-original', numericValue.toString());
            }
            
            // Get original price - always use this as the source of truth
            const originalPrice = parseFloat(moneyEl.getAttribute('data-tax-original')) || numericValue;
            
            if (!isNaN(originalPrice) && originalPrice > 0) {
              // Calculate price with tax based on ORIGINAL price
              const priceWithTax = originalPrice * (1 + taxRate);
              
              // Format and update price display
              moneyEl.textContent = formatMoney(priceWithTax, priceText);
              moneyEl.setAttribute('data-tax-processed', 'true');
              
              // Find the corresponding currency converter if any and hide it
              const nextConverterEl = moneyEl.nextElementSibling;
              if (nextConverterEl && nextConverterEl.classList.contains('currency-converter-wrapper-amount-box')) {
                nextConverterEl.style.display = 'none';
              }
              
              processed = true;
            }
          }
          // Case 2: Hidden original price with currency converter
          else if (hiddenMoneyEl && converterEl && !converterEl.getAttribute('data-tax-processed')) {
            // Get the price digits element directly - avoid multiple queries
            const digitsEl = converterEl.querySelector('.cbb-price-digits');
            
            if (digitsEl) {
              const priceText = digitsEl.textContent.trim();
              // Parse the price, handling European format (comma as decimal)
              const numericValue = parseCurrency(priceText);

              // Store original price if not already stored
              if (!converterEl.hasAttribute('data-tax-original')) {
                converterEl.setAttribute('data-tax-original', numericValue.toString());
              }

              // Get original price - always use this as the source of truth
              const originalPrice = parseFloat(converterEl.getAttribute('data-tax-original')) || numericValue;

              if (!isNaN(originalPrice) && originalPrice > 0) {
                // Calculate price with tax based on ORIGINAL price
                const priceWithTax = originalPrice * (1 + taxRate);

                // Check if the original format used comma as decimal (European format)
                const useCommaDecimal = priceText.indexOf(',') > priceText.indexOf('.');

                // Format the price appropriately
                let formattedPrice = priceWithTax.toFixed(2);
                if (useCommaDecimal) {
                  formattedPrice = formattedPrice.replace('.', ',');
                }

                // Update just the digits element - more efficient
                digitsEl.textContent = formattedPrice;
                
                // Make sure the color is visible - target specific element
                const amountBox = converterEl.querySelector('.currency-converter-amount-box');
                if (amountBox && (amountBox.style.color === 'rgba(0, 0, 0, 0)' || amountBox.style.color === 'transparent')) {
                  amountBox.style.color = 'rgb(102, 102, 102)';
                }
                
                // Mark the converter as processed
                converterEl.setAttribute('data-tax-processed', 'true');
                converterEl.setAttribute('data-color-fixed', 'true');
                
                processed = true;
              }
            }
          }
          
          // Add tax info if processed successfully
          if (processed && !container.querySelector('.gm-tax-info')) {
            const taxInfoEl = document.createElement('div');
            taxInfoEl.className = 'gm-tax-info';
            taxInfoEl.style.fontSize = '0.65rem';
            taxInfoEl.style.color = '#999';
            taxInfoEl.style.marginTop = '2px';
            taxInfoEl.innerHTML = `Incl. ${(taxRate * 100).toFixed(0)}% ${taxName}`;
            container.appendChild(taxInfoEl);
          }
          
          // Mark container as processed
          container.setAttribute('data-tax-processed', 'true');
        });
      }

      // Main function to process all prices on the page
      function processAllPrices() {
        // SECTION 1: Handle product-price-original and compare-price elements (main product page)
        handleMainProductPrices();

        // SECTION 2: Handle item-price elements (collection items, cart, etc.)
        handleItemPrices();

        // SECTION 3: Handle cart total price
        handleCartTotal();

        // SECTION 4: Handle collection grid prices
        handleCollectionPrices();
        
        // SECTION 5: Handle payment options
        handlePaymentOptions();
        
        // SECTION 6: Handle game module prices
        handleGameModulePrices();

        // After initial processing, remove the initial hide style to show processed prices
        const initialHideStyle = document.getElementById('tax-price-initial-hide');
        if (initialHideStyle) {
          initialHideStyle.remove();
        }
      }

      // More efficient currency parsing function - optimized for performance
      function parseCurrency(priceText) {
        // Quick exit for empty strings
        if (!priceText || !priceText.trim()) return NaN;
        
        // Use faster numeric extraction - drops currency symbols
        const numericString = priceText.replace(/[^\d.,]/g, '');
        
        // Cache string characteristics for faster lookups
        const hasComma = numericString.indexOf(',') !== -1;
        const hasDot = numericString.indexOf('.') !== -1;
        
        // Fast path for simple formats (most common case)
        if (!hasComma && !hasDot) {
          return parseFloat(numericString);
        }
        
        // US/UK format with simple thousands separators (next most common)
        if (hasComma && hasDot && numericString.lastIndexOf(',') < numericString.lastIndexOf('.')) {
          return parseFloat(numericString.replace(/,/g, ''));
        }
        
        // Handle European format
        if (hasComma && !hasDot) {
          // Check if comma is likely a decimal separator (2 digits after it)
          const commaPos = numericString.lastIndexOf(',');
          if (numericString.length - commaPos - 1 === 2) {
            return parseFloat(numericString.replace(',', '.'));
          }
          // Otherwise treat as thousands separator
          return parseFloat(numericString.replace(/,/g, ''));
        }
        
        // For remaining complex formats
        if (hasComma && hasDot) {
          // European format (dot is thousands, comma is decimal)
          return parseFloat(numericString.replace(/\./g, '').replace(',', '.'));
        }
        
        // Default case - simple number with a decimal point
        return parseFloat(numericString);
      }

      // Set up event listeners for dynamic content
      function setupEventListeners() {
        // 1. Listen for search input events
        const searchInputs = document.querySelectorAll('input[data-js-search-input]');
        searchInputs.forEach(function(input) {
          input.addEventListener('input', function() {
            // Delay slightly to allow search results to populate
            setTimeout(handleSearchResults, 500);
          });

          // Also listen for keyup events (Enter key)
          input.addEventListener('keyup', function(e) {
            if (e.key === 'Enter') {
              setTimeout(handleSearchResults, 800);
            }
          });
        });

        // 2. home SEO/perf pass 2026-10-01: the search results container is one of
        //    LATE_PRICE_ROOTS observed by watchLatePrices() (see 4. below), so the
        //    dedicated searchResultsObserver that used to live here is gone.

        // 3. Listen for clicks that might trigger search (magnifying glass icon, etc.)
        document.addEventListener('click', function(event) {
          // Check if the click target is a search-related element
          const searchTriggers = [
            'search-toggle',
            'search-button',
            'search-submit',
            'search-icon'
          ];

          const targetClassList = event.target.classList;
          const targetId = event.target.id;

          // Check if any search trigger classes or IDs are present
          const isSearchRelated = searchTriggers.some(function(trigger) {
            return targetClassList.contains(trigger) || targetId.includes(trigger);
          });

          if (isSearchRelated) {
            setTimeout(handleSearchResults, 800);
          }
        });

        // 4. home SEO/perf pass 2026-10-01: late prices are now detected by
        //    watchLatePrices(), which replaces the dynamicContentObserver, the
        //    IntersectionObserver gating (its disconnect(target) call actually
        //    dropped every observation, so in practice only the old setInterval
        //    caught late prices) and the document.body subtree observer (5.). Currency converter
        //    wrappers are included so payment-option/display fixes re-run when the
        //    converter app renders after us, which the 1s interval used to cover.
        watchLatePrices(processLatePrices, HIDDEN_PRICE_SELECTOR + ', .currency-converter-wrapper-amount-box:not([data-color-fixed="true"])');
      }

      // Function to handle collection grid prices
      function handleCollectionPrices() {
        // Target the flex price spans in collection grids
        const collectionPrices = document.querySelectorAll('.price.text-size--smaller span[style*="display:flex"]:not([data-tax-processed="true"])');

        collectionPrices.forEach(function(priceElement) {
          // Get the price text
          const originalPriceText = priceElement.textContent;
          const numericValue = parseCurrency(originalPriceText);

          if (!isNaN(numericValue)) {
            // Calculate price with tax
            const priceWithTax = numericValue * (1 + taxRate);

            // Format and update the price
            const formattedPrice = formatMoney(priceWithTax, originalPriceText);
            priceElement.textContent = formattedPrice;

            // Add the tax info after the price
            const priceContainer = priceElement.closest('.price');
            if (priceContainer) {
              // Check for existing tax info
              let taxInfoElement = priceContainer.querySelector('.collection-tax-info');

              if (!taxInfoElement) {
                // Create new tax info element
                taxInfoElement = document.createElement('span');
                taxInfoElement.className = 'collection-tax-info';
                taxInfoElement.style.display = 'block';
                taxInfoElement.style.fontSize = '0.65rem';
                taxInfoElement.style.color = '#666';
                taxInfoElement.style.marginTop = '2px';
                taxInfoElement.innerHTML = `Incl. ${(taxRate * 100).toFixed(0)}% ${taxName}`;

                // Append to the price container
                priceContainer.appendChild(taxInfoElement);
              }

              // Find and process the compare-at price in <del> element (for collection items - legacy)
              const delElement = priceContainer.querySelector('del:not([data-tax-processed="true"])');
              if (delElement) {
                const compareText = delElement.textContent.trim();
                const compareValue = parseCurrency(compareText);

                if (!isNaN(compareValue) && compareValue > 0) {
                  // Calculate compare price with tax
                  const comparePriceWithTax = compareValue * (1 + taxRate);

                  // Format and update the compare price
                  const formattedComparePrice = formatMoney(comparePriceWithTax, compareText);
                  delElement.textContent = formattedComparePrice;

                  // Mark as processed
                  delElement.setAttribute('data-tax-processed', 'true');
                }
              }

              // Find and process compare-at price with data-js-product-price-compare attribute (new structure)
              const comparePriceElement = priceContainer.querySelector('[data-js-product-price-compare]:not([data-tax-processed="true"])');
              if (comparePriceElement && comparePriceElement.textContent.trim()) {
                const compareText = comparePriceElement.textContent.trim();
                const compareValue = parseCurrency(compareText);

                if (!isNaN(compareValue) && compareValue > 0) {
                  // Calculate compare price with tax
                  const comparePriceWithTax = compareValue * (1 + taxRate);

                  // Format and update the compare price
                  const formattedComparePrice = formatMoney(comparePriceWithTax, compareText);
                  comparePriceElement.textContent = formattedComparePrice;

                  // Mark as processed
                  comparePriceElement.setAttribute('data-tax-processed', 'true');
                }
              }
            }

            // Mark as processed
            priceElement.setAttribute('data-tax-processed', 'true');
          }
        });
      }

      // Function specifically to handle search results
      function handleSearchResults() {
        // Find all search result containers
        const searchResultsContainer = document.querySelector('[data-js-search-results]');
        if (!searchResultsContainer) return;

        // Process all item prices within search results
        const searchItemPrices = searchResultsContainer.querySelectorAll('.item-price:not([data-tax-processed="true"])');

        searchItemPrices.forEach(function(priceContainer) {
          // Find the price element (strong)
          const strongElement = priceContainer.querySelector('strong');
          if (!strongElement) return;

          const originalPriceText = strongElement.textContent;
          const numericValue = parseCurrency(originalPriceText);

          if (isNaN(numericValue)) return;

          // Calculate price with tax
          const priceWithTax = numericValue * (1 + taxRate);

          // Format and update the price
          const formattedPrice = formatMoney(priceWithTax, originalPriceText);
          strongElement.textContent = formattedPrice;

          // Add tax info directly under this price
          const parentElement = priceContainer.parentNode; // This is likely the 'caption' span

          // Look for existing tax info
          let existingTaxInfo = null;
          for (let i = 0; i < parentElement.childNodes.length; i++) {
            const node = parentElement.childNodes[i];
            if (node.classList && node.classList.contains('search-tax-info')) {
              existingTaxInfo = node;
              break;
            }
          }

          // Create or update tax info
          if (!existingTaxInfo) {
            const taxInfoElement = document.createElement('span');
            taxInfoElement.className = 'search-tax-info text-size--smallest';
            taxInfoElement.style.display = 'block';
            taxInfoElement.style.fontSize = '0.65rem';
            taxInfoElement.style.color = '#666';
            taxInfoElement.style.marginTop = '2px';
            taxInfoElement.innerHTML = `Incl. ${(taxRate * 100).toFixed(0)}% ${taxName}`;

            // Insert after the price container
            parentElement.appendChild(taxInfoElement);
          }

          // Mark as processed
          priceContainer.setAttribute('data-tax-processed', 'true');
          strongElement.setAttribute('data-tax-processed', 'true');
        });
      }

      // Function to handle main product prices with data-js attributes
      function handleMainProductPrices() {
        const priceReviewsContainers = document.querySelectorAll('.price-reviews:not([data-tax-processed="true"])');

        priceReviewsContainers.forEach(function(container) {
          // Handle original price
          const priceElement = container.querySelector('[data-js-product-price-original]');
          if (priceElement) {
            const originalPriceText = priceElement.textContent;
            const numericValue = parseCurrency(originalPriceText);

            if (!isNaN(numericValue)) {
              // Calculate price with tax
              const priceWithTax = numericValue * (1 + taxRate);

              // Format money in the same format as original price
              const formattedPrice = formatMoney(priceWithTax, originalPriceText);

              // Update price display
              priceElement.textContent = formattedPrice;

              // Mark price element as processed
              priceElement.setAttribute('data-tax-processed', 'true');
            }
          }

          // Handle compare price if present
          const comparePriceElement = container.querySelector('[data-js-product-price-compare]');
          if (comparePriceElement && comparePriceElement.textContent.trim()) {
            const compareText = comparePriceElement.textContent.trim();
            const compareValue = parseCurrency(compareText);

            if (!isNaN(compareValue)) {
              const comparePriceWithTax = compareValue * (1 + taxRate);
              comparePriceElement.textContent = formatMoney(comparePriceWithTax, compareText);

              // Mark compare price element as processed
              comparePriceElement.setAttribute('data-tax-processed', 'true');
            }
          }

          // Add tax info directly after the price div
          const priceDiv = container.querySelector('.price');
          const unitPriceSpan = container.querySelector('[data-js-product-price-unit]');

          if (priceDiv) {
            // Remove existing tax info if any
            const existingTaxInfo = container.querySelector('.product-tax-info');
            if (existingTaxInfo) {
              existingTaxInfo.remove();
            }

            // Create new tax info element
            const taxInfoElement = document.createElement('div');
            taxInfoElement.className = 'product-tax-info text-size--smallest';
            taxInfoElement.style.fontSize = '0.75rem';
            taxInfoElement.style.color = '#666';
            taxInfoElement.style.marginTop = '2px';
            taxInfoElement.innerHTML = `Includes ${(taxRate * 100).toFixed(0)}% ${taxName}`;

            // Insert the tax info after the price div and before the unit price span
            if (unitPriceSpan) {
              container.insertBefore(taxInfoElement, unitPriceSpan);
            } else {
              container.appendChild(taxInfoElement);
            }
          }

          // Mark container as processed
          container.setAttribute('data-tax-processed', 'true');
        });
      }

      // Function to handle item prices (collection items, cart, etc.)
      function handleItemPrices() {
        const itemPrices = document.querySelectorAll('.item-price:not([data-tax-processed="true"])');

        itemPrices.forEach(function(priceContainer) {
          // Skip if inside search results (handled separately)
          if (priceContainer.closest('[data-js-search-results]')) return;

          // Find the strong element with the price
          const strongElement = priceContainer.querySelector('strong');
          if (strongElement) {
            const originalPriceText = strongElement.textContent;
            const numericValue = parseCurrency(originalPriceText);

            if (!isNaN(numericValue)) {
              // Calculate price with tax
              const priceWithTax = numericValue * (1 + taxRate);

              // Format money in the same format as original price
              const formattedPrice = formatMoney(priceWithTax, originalPriceText);

              // Update price display
              strongElement.textContent = formattedPrice;

              // Mark strong element as processed
              strongElement.setAttribute('data-tax-processed', 'true');

              // Add tax info if not already present
              const existingTaxInfo = priceContainer.nextElementSibling;
              if (!existingTaxInfo || !existingTaxInfo.classList.contains('product-tax-info')) {
                // Create new tax info element
                const taxInfoElement = document.createElement('div');
                taxInfoElement.className = 'product-tax-info text-size--smallest';
                taxInfoElement.style.fontSize = '0.7rem';
                taxInfoElement.style.color = '#666';
                taxInfoElement.style.marginTop = '2px';
                taxInfoElement.innerHTML = `Includes ${(taxRate * 100).toFixed(0)}% ${taxName}`;

                // Insert after the price container
                if (priceContainer.nextElementSibling) {
                  priceContainer.parentNode.insertBefore(taxInfoElement, priceContainer.nextElementSibling);
                } else {
                  priceContainer.parentNode.appendChild(taxInfoElement);
                }
              }
            }
          }

          // Find and process the compare-at price in <del> element (for cart items)
          const delElement = priceContainer.querySelector('del');
          if (delElement) {
            // The del element may contain a .money span or direct text
            const moneySpan = delElement.querySelector('.money');
            const targetElement = moneySpan || delElement;

            if (!targetElement.hasAttribute('data-tax-processed') || targetElement.getAttribute('data-tax-processed') !== 'true') {
              const compareText = targetElement.textContent.trim();
              const compareValue = parseCurrency(compareText);

              if (!isNaN(compareValue) && compareValue > 0) {
                // Calculate compare price with tax
                const comparePriceWithTax = compareValue * (1 + taxRate);

                // Format and update the compare price
                const formattedComparePrice = formatMoney(comparePriceWithTax, compareText);
                targetElement.textContent = formattedComparePrice;

                // Mark as processed
                targetElement.setAttribute('data-tax-processed', 'true');
              }
            }
          }

          // Mark price container as processed
          priceContainer.setAttribute('data-tax-processed', 'true');
        });
      }

      // Function to handle cart total price
      function handleCartTotal() {
        const cartTotal = document.getElementById('CartTotal');
        if (cartTotal && !cartTotal.hasAttribute('data-tax-processed')) {
          // Find the price element within the cart total (text-size--large span)
          const totalPriceElement = cartTotal.querySelector('.text-size--large');
          if (totalPriceElement) {
            const originalPriceText = totalPriceElement.textContent;
            const numericValue = parseCurrency(originalPriceText);

            if (!isNaN(numericValue)) {
              // Calculate price with tax
              const priceWithTax = numericValue * (1 + taxRate);

              // Format money in the same format as original price
              const formattedPrice = formatMoney(priceWithTax, originalPriceText);

              // Update price display
              totalPriceElement.textContent = formattedPrice;

              // Mark price element as processed
              totalPriceElement.setAttribute('data-tax-processed', 'true');

              // Add tax info if not already present
              const existingTaxInfo = cartTotal.nextElementSibling;
              if (!existingTaxInfo || !existingTaxInfo.classList.contains('cart-tax-info')) {
                // Create new tax info element
                const taxInfoElement = document.createElement('div');
                taxInfoElement.className = 'cart-tax-info text-size--smallest';
                taxInfoElement.style.fontSize = '0.7rem';
                taxInfoElement.style.color = '#666';
                taxInfoElement.style.marginTop = '2px';
                taxInfoElement.style.textAlign = 'right';
                taxInfoElement.innerHTML = `Includes ${(taxRate * 100).toFixed(0)}% ${taxName}`;

                // Insert after the cart total
                if (cartTotal.nextElementSibling) {
                  cartTotal.parentNode.insertBefore(taxInfoElement, cartTotal.nextElementSibling);
                } else {
                  cartTotal.parentNode.appendChild(taxInfoElement);
                }
              }
            }
          }

          // Mark cart total as processed
          cartTotal.setAttribute('data-tax-processed', 'true');
        }
      }

      // Optimized money formatting function - more efficient
      function formatMoney(amount, template) {
        // Quick exit for invalid values
        if (isNaN(amount) || !template) return template;
        
        // Extract currency symbol - faster implementation with simple regex
        let currencySymbol = '$'; // Default
        const symbolMatch = template.match(/^\D+/);
        if (symbolMatch) {
          currencySymbol = symbolMatch[0];
        }
        
        // Determine format type quickly using indexOf (faster than multiple regex)
        const hasComma = template.indexOf(',') !== -1;
        const hasDot = template.indexOf('.') !== -1;
        
        // Determine if using comma as decimal separator
        const useComma = hasComma && 
                       (!hasDot || template.lastIndexOf('.') < template.lastIndexOf(','));
        
        // Format the number with fixed decimal places
        let formattedAmount = amount.toFixed(2);
        
        // European format: replace decimal point with comma if needed
        if (useComma) {
          formattedAmount = formattedAmount.replace('.', ',');
        }
        
        // Only add thousands separators if the original format had them
        // (Optimized to avoid unnecessary operations)
        const hasThousands = (useComma && hasDot) || (!useComma && hasComma);
        
        if (hasThousands) {
          // Split number at decimal point/comma
          const parts = formattedAmount.split(useComma ? ',' : '.');
          
          // Add thousands separators to integer part
          // Cache values for better performance
          const integerPart = parts[0];
          const len = integerPart.length;
          
          // Only process if number is large enough to need separators
          if (len > 3) {
            let result = '';
            // Manual thousands separator addition (faster than regex for short strings)
            for (let i = 0; i < len; i++) {
              if (i > 0 && (len - i) % 3 === 0) {
                result += useComma ? '.' : ',';
              }
              result += integerPart.charAt(i);
            }
            parts[0] = result;
          }
          
          formattedAmount = parts.join(useComma ? ',' : '.');
        }
        
        // Return formatted result
        return currencySymbol + formattedAmount;
      }

      // Initial run
      processAllPrices();

      // home SEO/perf pass 2026-10-01: the 1s setInterval that used to follow is
      // gone. Everything it re-ran is now triggered by events instead:
      //   - late prices (game modules, search, product grids, cart, payment
      //     options, converter wrappers) -> watchLatePrices() -> processLatePrices()
      //   - payment options after variant/plan changes -> the 'change' and
      //     'click' listeners above, plus the wrapper observer
      //   - fixDisplayIssues() -> requestFixDisplayIssues() after every pass

      // A full pass, the same handlers the interval ran, for prices that arrive
      // after load. Every handler skips elements already marked processed, so
      // running it on each late insertion is idempotent.
      function processLatePrices() {
        requeueUnpricedGameModules();
        handleSearchResults();
        processAllPrices();
        requestFixDisplayIssues();
      }

      // 2026-10-01 browser verification follow-up: handleGameModulePrices()
      // marks a Globo container processed even when its .money wasn't rendered
      // yet, so a price Globo fills in afterwards would stay hidden. Un-mark
      // such containers (a container it actually priced has .gm-tax-info) so
      // this pass prices them.
      function requeueUnpricedGameModules() {
        document.querySelectorAll('.gm-product-price[data-tax-processed="true"]').forEach(function(container) {
          if (!container.querySelector('.gm-tax-info') &&
              container.querySelector('.money:not([data-tax-processed="true"])')) {
            container.removeAttribute('data-tax-processed');
          }
        });
      }

      // fixDisplayIssues() throttles itself to once per 2s, which was fine while
      // the interval kept calling it. Without the interval a throttled call
      // would simply be lost, so queue one trailing call instead of dropping it.
      let fixDisplayTimer = null;
      function requestFixDisplayIssues() {
        if (fixDisplayTimer !== null) return;
        const wait = Math.max(0, 2000 - (Date.now() - lastFixTime));
        fixDisplayTimer = setTimeout(function() {
          fixDisplayTimer = null;
          fixDisplayIssues();
        }, wait);
      }

      // One trailing display fix-up after the initial pass replaces the
      // interval's randomised first call a few seconds after load. (Called
      // after the `let` above on purpose: before it, fixDisplayTimer is in its
      // temporal dead zone.)
      requestFixDisplayIssues();
    } else {
      // If no tax rate for country, run the mark process once and set up observers for dynamic content
      markAllPricesAsProcessed();

      // home SEO/perf pass 2026-10-01: replaces the dynamicContainers observers
      // (most of whose selectors match nothing in this theme), the document.body
      // subtree observer and the 2s setInterval. Any late price under a known
      // root or appended to <body> is revealed before the next paint.
      watchLatePrices(markAllPricesAsProcessed, HIDDEN_PRICE_SELECTOR);
    }
  }
})();
